/** @jsxImportSource react */

import { StateAbbr } from '@common/data/povertyRate';
import { useShare } from '@common/helpers/share';
import NeighborhoodSurvey from '@react/data-hub/components/NeighborhoodSurvey';
import useSearchInsights from '@react/data-hub/hooks/useSearchInsights';
import { InsightsFilters } from '@react/data-hub/types/misc';
import { stateAbbr } from '@sly/core/src/constants/geo';
import { useRouter } from 'next/router';

import CommonToolForm from './CommonToolForm';
import { ToolHero } from './Styles';
import { defaultSearchInsightsFilter } from '../constants/data';
import { ToolDetails } from '../types';

export default function NeighborhoodInsightsTool({
  heading,
  description,
  shortDescription,
}: ToolDetails) {
  const { query, push, pathname } = useRouter();
  const { city, state, toolName } = query || {};
  const { searchInsights, locationStringWithCountryCode } = useSearchInsights({
    filters: {
      toc: defaultSearchInsightsFilter.careType,
      city,
      state,
      roomType: defaultSearchInsightsFilter.roomType,
      include: defaultSearchInsightsFilter.include,
    } as InsightsFilters,
  });

  const { share } = useShare();

  const handleSubmit = (values: any) => {
    const currentFilters = {
      state: '',
      city: '',
      toolName,
    };
    const searchParams = values?.location?.searchParams;

    if (searchParams?.state) {
      currentFilters.state = searchParams.state;
    }
    if (searchParams?.city) {
      currentFilters.city = searchParams.city;
    }

    if (!currentFilters.city || !currentFilters.state) {
      return;
    }

    push(
      {
        pathname,
        query: currentFilters,
      },
      undefined,
      { shallow: false }
    );
  };

  const formComponent = (
    <CommonToolForm
      initialFilters={{
        location: {
          displayText: locationStringWithCountryCode,
          searchParams: {
            state,
            city,
          },
        },
      }}
      handleSubmit={handleSubmit}
      locationString={locationStringWithCountryCode}
      nearest={searchInsights?.nearest}
      buttonClassName='w-full md:ml-auto md:w-max'
      buttonText='Search'
      eventProps={{ location: `${heading} tool` }}
    />
  );

  if (!searchInsights) {
    return (
      <>
        <ToolHero
          heading={heading}
          subheading={
            description ||
            'What you need to know about the area you are searching'
          }
        />
        {formComponent}
      </>
    );
  }

  const { neighborhoodSurvey, stateName, name: cityName } = searchInsights;

  const stateAbbreviation =
    stateAbbr[stateName as keyof typeof stateAbbr] || '';

  return (
    <>
      <ToolHero
        heading={heading}
        subheading={
          cityName
            ? `What you need to know about ${cityName} area`
            : description
        }
        onShare={() => {
          share({
            title: heading,
            text: shortDescription || description || '',
            url: window.location.href,
          });
        }}
      />
      {formComponent}
      <NeighborhoodSurvey
        neighborhoodSurvey={neighborhoodSurvey}
        stateAbbr={stateAbbreviation as StateAbbr}
        openByDefault
      />
    </>
  );
}
